import React, { useCallback, useContext, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

import { compose } from 'react-apollo';
import { withTheme } from 'theming';
import Configuration from 'Configuration';
import { get } from 'lodash';
import CheckBox from 'MoshtixShared/component-check-box';
import Button from 'MoshtixShared/component-button';

import { withState } from 'MoshtixShared/component-context-provider';
import { A, Div, H2, P } from 'MoshtixShared/component-element';
import { ResponsiveGridClass } from 'MoshtixShared/component-responsive-grid';
import Section from 'MoshtixShared/component-section';
import { configuration } from 'MoshtixShared/helper-responsive-configuration';

import { TicketListSkeleton } from 'Components/component-skeletons';
import { TicketDistroContext } from '../../../app/contexts';
import { GetMobileDialog } from '../../../components/component-get-mobile-dialog';
import { ResellDialog } from '../../../components/component-resell-dialog';
import { TicketDialogBar } from '../component-ticket-dialog-bar-mobile';
import { TicketListItem } from '../component-ticket-list-item';

import { OrderInterface, TicketInterface } from '../../../types/app';
import { ticketCanResell, orderCanResell } from '../helper-resale-checker';
import styles from './styles';

const { Hide } = new ResponsiveGridClass({ configuration });

const TicketListWithoutTheme = (props) => {
  const { ticketDistroContext } = useContext(TicketDistroContext);
  const [selected, setSelected] = useState<number[]>([]);
  const [getMobileVisible, setGetMobileVisible] = useState(false);
  const [resellVisible, setResellVisible] = useState(false);

  const handleToggleTicketItem = ({ ticketId }: { ticketId: number }): void => {
    const newSelected = selected.includes(ticketId)
      ? selected.filter((val) => val !== ticketId)
      : selected.concat(ticketId); // staying away from mutating selected with push
    setSelected(newSelected);
  };

  const contextState = ticketDistroContext.state;
  const allOrders: OrderInterface[] = get(contextState, 'orders.items', []);
  const assignedTickets: TicketInterface[] = get(contextState, 'assignedTickets.items', []);

  const allAvailableTickets = allOrders
    .flatMap(({ tickets }: OrderInterface) => tickets.items || [])
    .concat(assignedTickets)
    .filter(Boolean)
    .filter((ticket) => ticket.ticketType != null);

  const [firstOrder] = allOrders;

  const { event = {} } = firstOrder || get(contextState, 'assignedTickets.items[0]', {});

  const resalePeriods = get(event, 'resalePeriods.items', []);
  const ordersThatCanResell = orderCanResell({ orders: allOrders, resalePeriods });
  const clientId = get(event, 'client.id', 0);
  // enable download pdf for ATC since they don't want to use ticket modal
  const disableTicketDialog = clientId === 9154;

  const handleToggleAll = ({ ticketIds }: { ticketIds: number[] }): void =>
    selected.length ? setSelected([]) : setSelected(ticketIds);

  const getTicketListItem = useCallback(
    (ticket, order, isFromAssignedList = false) => {
      const periods = get(event, 'resalePeriods.items', []);

      // Handle case where ticketType might be null
      const ticketTypeId = get(ticket, 'ticketType.id');
      if (!ticketTypeId) {
        // Skip tickets without ticket type
        return null;
      }

      return (
        <TicketListItem
          ticket={ticket}
          order={order}
          event={event}
          isFromAssignedList={isFromAssignedList}
          disableTicketDialog={disableTicketDialog}
          canResell={ticketCanResell({ resalePeriods: periods, ticketTypeId })}
          checked={selected.includes(ticket.id)}
          key={`ticket-list-${ticket.id}`}
          onChange={(): void => handleToggleTicketItem({ ticketId: ticket.id })}
        />
      );
    },
    [selected, handleToggleTicketItem, event],
  );

  const ticketListItems = useMemo(() => {
    // Get all tickets from orders
    const orderTickets = allOrders.flatMap((order) =>
      order.tickets.items.map((ticket) => getTicketListItem(ticket, order)).filter(Boolean),
    );

    // Get all assigned tickets and mark them as assigned
    const assignedTicketItems = assignedTickets
      .map((ticket) => getTicketListItem(ticket, ticket.order || {}, true))
      .filter(Boolean);

    return { orderTickets, assignedTicketItems };
  }, [allOrders, assignedTickets, getTicketListItem]);

  const ticketsList = useMemo(() => {
    const hasOrderTickets = ticketListItems.orderTickets.filter(Boolean).length > 0;
    const hasAssignedTickets = ticketListItems.assignedTicketItems.filter(Boolean).length > 0;
    const showHeadings = hasOrderTickets && hasAssignedTickets;

    return (
      <>
        {hasOrderTickets && (
          <>
            {showHeadings && <H2 css={styles.headingCss({ props })}>Purchased Tickets</H2>}
            {ticketListItems.orderTickets}
          </>
        )}

        {hasAssignedTickets && (
          <>
            {showHeadings && <H2 css={styles.headingCss({ props })}>Assigned Tickets</H2>}
            {ticketListItems.assignedTicketItems}
          </>
        )}
      </>
    );
  }, [ticketListItems]);

  if (!contextState.dataLoaded) {
    return (
      <>
        <Section css={styles.backgroundCss({ props })} innerContainerCss={styles.innerBackgroundCss()}>
          <TicketListSkeleton />
        </Section>
      </>
    );
  }

  return (
    <Section css={styles.backgroundCss({ props })} innerContainerCss={styles.innerBackgroundCss()}>
      {getMobileVisible && (
        <GetMobileDialog
          onClose={() => {
            setGetMobileVisible(false);
          }}
        />
      )}
      {resellVisible && (
        <ResellDialog
          orders={ordersThatCanResell}
          onClose={() => {
            setResellVisible(false);
          }}
        />
      )}
      {!disableTicketDialog && (
        <Hide above="medium">
          <TicketDialogBar tickets={allAvailableTickets} event={event} />
        </Hide>
      )}

      {ticketsList}

      <Div css={styles.tempSpaceCss({ props })} />
      <Div css={styles.furtherInformation}>
        <H2 css={styles.headingCss({ props })}>Further information</H2>
        {event.ageRestriction === 'OVER18' && (
          <Div css={styles.ageWarningCss({ props })}>You must be 18 or over to attend this event.</Div>
        )}
        <Div
          css={styles.wysiwygCss}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(event.ticketFinePrint || '', {
              ALLOWED_TAGS: [
                'p',
                'br',
                'b',
                'i',
                'u',
                'strong',
                'em',
                'a',
                'ul',
                'ol',
                'li',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'div',
                'span',
                'blockquote',
                'hr',
              ],
              ALLOWED_ATTR: ['href', 'rel', 'dir', 'class', 'style'],
              ALLOW_DATA_ATTR: false,
            }),
          }}
        />
        <P>
          View the{' '}
          <A href={`https://${Configuration.publicWebsiteOriginalUrl}/v2/event/${event.id}/terms-and-conditions`}>
            Terms &amp; Conditions
          </A>{' '}
          for this event
        </P>
      </Div>
    </Section>
  );
};

export const TicketList = compose(withTheme)(TicketListWithoutTheme);
